import { useState, useRef, useEffect } from "react";

const P  = "rgb(160,0,48)";
const PL = "rgba(160,0,48,0.08)";
const PM = "rgba(160,0,48,0.18)";
const PD = "rgb(110,0,32)";
const MAX_SECONDS = 60;

// ─── YUUKKE service categories → subcategories → script ──────────────────────
const SERVICE_MAP = {
  "Consulting": {
    subcategories: ["Academic Consulting", "Business Consulting"],
    scripts: {
      "Academic Consulting": `Hi, I'm [Your Name], an academic consultant based in [City/Country].

I help students — from school to postgraduate level — improve their academic performance, choose the right courses, and build the skills they need to succeed.

With [X] years of experience, I've guided over [number] students through exams, university applications, study plans, and career decisions.

My approach is personalised — I work with each student's strengths, not a one-size-fits-all method.

If you want real academic results with expert guidance, I'd love to connect. Let's make your journey a success.`,

      "Business Consulting": `Hi, I'm [Your Name], a business consultant with [X] years of experience helping companies grow, improve operations, and solve complex challenges.

I work with startups, SMEs, and growing businesses across [your industry focus] to develop clear strategies, improve processes, and drive measurable results.

Whether you need help with business planning, market entry, team structure, or operational efficiency — I bring a practical, hands-on approach that gets results.

My clients often say I cut through the complexity and give them a clear path forward quickly.

If you're ready to take your business to the next level, let's connect.`,
    }
  },

  "Education & Training": {
    subcategories: ["Career Coaching", "Coaching & Mentoring", "K12 and Higher Education Tuition", "Leadership and Management"],
    scripts: {
      "Career Coaching": `Hi, I'm [Your Name], a career coach helping professionals find clarity, confidence, and their next big opportunity.

I work with job seekers, career changers, and professionals at a crossroads — from building standout CVs and preparing for interviews to negotiating offers and planning long-term career growth.

Over [X] years, I've helped [number] professionals land roles they love at companies across [industries].

My approach is direct and results-focused — no generic advice, just a clear roadmap built around your goals.

If you're ready to take control of your career, let's talk.`,

      "Coaching & Mentoring": `Hi, I'm [Your Name], a coach and mentor with [X] years of experience helping individuals unlock their potential and achieve meaningful goals.

I work with professionals, entrepreneurs, and individuals who want to grow — whether that's in their career, business, relationships, or personal life.

What makes my coaching different is the depth of our work together. I don't just give advice — I help you discover your own answers and build the habits and mindset for lasting change.

Over [number] sessions with clients across different backgrounds, I've seen that the right guidance at the right time changes everything.

Let's have a conversation. I'd love to be part of your growth journey.`,

      "K12 and Higher Education Tuition": `Hi, I'm [Your Name], an experienced tutor specialising in [your subject — e.g. Maths, Science, English] for students from primary school through to university level.

I help students build confidence, close knowledge gaps, and genuinely understand the subjects they're struggling with — not just memorise answers for exams.

With [X] years of tutoring experience, I've helped [number] students improve their grades and develop real subject knowledge that stays with them.

My sessions are patient, structured, and tailored to each student's pace and learning style.

Whether your child needs exam support or ongoing academic help, I'm here to make a real difference. Let's get started.`,

      "Leadership and Management": `Hi, I'm [Your Name], a leadership and management specialist with [X] years of experience developing high-performing leaders and teams.

I work with managers, senior professionals, and organisations who want to build stronger leadership culture, improve team performance, and navigate change with confidence.

My programmes combine practical frameworks with real-world application — so the learning doesn't stay in the classroom, it changes how you lead every day.

I've worked with teams and leaders across [industries/company sizes] and have seen firsthand how the right leadership development transforms organisations.

If you want to build leaders who inspire, let's connect.`,
    }
  },

  "Financial & Legal": {
    subcategories: ["Finance", "Investment", "Legal Support"],
    scripts: {
      "Finance": `Hi, I'm [Your Name], a financial consultant helping individuals and businesses take control of their finances and build lasting financial security.

I specialise in [your area — e.g. personal budgeting, business finance, tax planning, cash flow management] and have helped [number] clients over [X] years make smarter financial decisions.

Whether you're trying to manage debt, plan for the future, grow your business financially, or simply understand where your money is going — I provide clear, honest, practical advice.

No jargon, no complicated spreadsheets — just straightforward financial guidance that makes a real difference.

Let's build the financial foundation you deserve. Get in touch today.`,

      "Investment": `Hi, I'm [Your Name], an investment consultant with [X] years of experience helping individuals and businesses grow their wealth strategically.

I work with clients who want to invest smarter — whether you're just starting out or looking to diversify an existing portfolio across equities, property, mutual funds, or alternative assets.

My approach is personalised to your risk profile, timeline, and financial goals — because no two investors are the same.

I've helped [number] clients across [India / your region] build investment strategies that work in the real world, not just on paper.

Ready to make your money work harder? Let's talk.`,

      "Legal Support": `Hi, I'm [Your Name], a legal consultant with expertise in [your area — e.g. contracts, corporate law, family law, property].

I help individuals and businesses understand their legal rights, navigate complex situations, and make confident decisions — without the confusion and jargon that often comes with legal matters.

With [X] years of experience, I've supported [number] clients through contracts, disputes, business registrations, and more.

My approach is clear, practical, and client-focused. I make sure you understand every step and every option available to you.

Whether you need a document reviewed or ongoing legal guidance, I'm here to help. Let's connect.`,
    }
  },

  "Personal Well-being": {
    subcategories: ["Counseling and Therapy", "Cybersecurity", "Mental Health", "Personal Development", "Technology"],
    scripts: {
      "Counseling and Therapy": `Hi, I'm [Your Name], a qualified counsellor and therapist based in [City/Country].

I support individuals through a wide range of challenges — from anxiety, stress, and relationship difficulties to grief, low confidence, and life transitions.

In a safe, non-judgmental space, I help you make sense of what you're experiencing, develop healthier patterns, and move forward with more clarity and resilience.

With [X] years of practice and [number] clients supported, I've seen what a difference the right therapeutic support can make.

You don't have to navigate this alone. Reach out, and let's take the first step together.`,

      "Mental Health": `Hi, I'm [Your Name], a mental health professional with [X] years of experience supporting individuals through anxiety, depression, burnout, and everyday emotional challenges.

I believe mental health is just as important as physical health — and seeking support is a sign of strength, not weakness.

My approach is warm, evidence-based, and completely confidential. Together, we'll work on practical strategies to help you manage your emotions, reduce stress, and build a life that feels more balanced and fulfilling.

I've supported [number] individuals across different backgrounds and life stages — and I meet every client exactly where they are.

If you're ready to prioritise your mental well-being, I'd love to support you. Let's connect.`,

      "Personal Development": `Hi, I'm [Your Name], a personal development specialist helping individuals become the best version of themselves — in their career, relationships, mindset, and daily life.

I work with people who are ready for real change — those who know something needs to shift but aren't sure where to start or how to stay consistent.

Through coaching, practical tools, and proven frameworks, I help clients set meaningful goals, break unhelpful patterns, and build confidence and clarity.

Over [X] years and [number] clients, I've seen that sustainable personal growth is possible for anyone with the right support and the right approach.

If you're ready to invest in yourself, let's start the conversation.`,

      "Technology": `Hi, I'm [Your Name], a technology consultant with [X] years of experience helping individuals and businesses navigate the digital world.

I specialise in [your area — e.g. IT strategy, software selection, digital transformation, tech support] and work with clients who want to use technology more effectively — without the overwhelm.

Whether you're a business looking to modernise your systems, or an individual needing tech guidance, I make complex technology simple and accessible.

I've supported [number] clients across [your industries] and pride myself on translating technical complexity into clear, actionable solutions.

Ready to make technology work for you? Let's connect.`,

      "Cybersecurity": `Hi, I'm [Your Name], a cybersecurity specialist helping businesses and individuals protect their data, systems, and digital identity.

In a world where cyber threats are growing every day, I help clients understand their vulnerabilities, implement the right protections, and respond confidently if something goes wrong.

I specialise in [your area — e.g. risk assessments, security audits, employee training, incident response] and have worked with [number] organisations over [X] years.

My approach is practical and plain-speaking — I don't just find problems, I help you fix them in a way that makes sense for your business and budget.

Let's make sure you're protected. Reach out today.`,
    }
  },

  "Lifestyle": {
    subcategories: ["Fashion & Style", "Health & Wellness", "Home & Living", "Travel Advisor"],
    scripts: {
      "Fashion & Style": `Hi, I'm [Your Name], a fashion and style consultant helping individuals discover a look that's authentic, confident, and completely them.

Whether you're building a professional wardrobe, preparing for a special occasion, or simply wanting to feel better about how you present yourself every day — I'm here to help.

I work with clients of all ages, sizes, and budgets to develop a style that works for their lifestyle and personality, not just what's trending.

Over [X] years and [number] clients, I've seen how the right style can completely transform how someone feels about themselves.

Ready to feel great every time you get dressed? Let's work together.`,

      "Health & Wellness": `Hi, I'm [Your Name], a health and wellness specialist dedicated to helping people feel their best — physically, mentally, and emotionally.

I work with clients who want to improve their energy, reduce stress, build healthier habits, or simply take better care of themselves — all through practical, sustainable approaches.

I specialise in [your area — e.g. nutrition, mindfulness, holistic wellness, women's health, stress management] and have worked with [number] clients over [X] years.

I believe wellness isn't a destination — it's a daily practice. And I'm here to make that practice enjoyable, achievable, and completely personalised to you.

Ready to feel better and live better? Let's start your wellness journey together.`,

      "Home & Living": `Hi, I'm [Your Name], a home and living specialist helping individuals and families create spaces that are functional, beautiful, and truly feel like home.

I offer [your service — e.g. interior styling, home organisation, decluttering, space planning] and work with clients who want their home to reflect who they are and how they want to live.

Over [X] years, I've transformed [number] homes — from small apartments to large family houses — always working within my clients' budgets and preferences.

My approach is collaborative, practical, and deeply personal. Your home, your way.

If you're ready to love the space you live in, let's connect.`,

      "Travel Advisor": `Hi, I'm [Your Name], a travel advisor with [X] years of experience planning unforgettable journeys for individuals, couples, and families.

I specialise in [your niche — e.g. luxury travel, solo travel, family holidays, honeymoons, adventure travel, India domestic travel] and take care of everything — from itineraries and bookings to visa support and on-trip assistance.

I don't believe in one-size-fits-all holidays. Every trip I plan is tailored to your budget, interests, and travel style — so you get an experience that's truly yours.

I've planned [number] trips across [number] countries and know how to find the hidden gems that make a journey truly memorable.

Ready to start planning your next adventure? Let's talk.`,
    }
  },
};

// ─── General fallback script ──────────────────────────────────────────────────
const GENERAL_SCRIPT = `Hi, I'm [Your Name], a [your profession] based in [City/Country].

I help [your target clients — e.g. individuals, families, businesses] with [what you do — e.g. solving specific problems, providing expert advice, delivering your service].

I have [X] years of experience and have worked with over [number] clients. What makes my approach unique is [your unique quality — e.g. your personal style, method, philosophy, results].

My clients often say I'm [a quality clients value about you — e.g. patient, reliable, results-driven, clear, easy to work with].

If you're looking for someone who genuinely cares about getting you the right results — I'd love to connect. Feel free to reach out and let's see how I can help you.`;

function fmt(s) {
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2,"0")}:${String(s % 60).padStart(2,"0")}`;
}

// ─── Teleprompter ─────────────────────────────────────────────────────────────
function Teleprompter({ script, isRecording, speed = 1 }) {
  const ref    = useRef(null);
  const animRef= useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    if (!ref.current) return;
    if (isRecording) {
      const pxPerSec = 18 * speed;
      let last = null;
      function step(ts) {
        if (last === null) last = ts;
        const dt = (ts - last) / 1000; last = ts;
        posRef.current += pxPerSec * dt;
        if (ref.current) ref.current.scrollTop = posRef.current;
        animRef.current = requestAnimationFrame(step);
      }
      animRef.current = requestAnimationFrame(step);
    } else {
      cancelAnimationFrame(animRef.current);
    }
    return () => cancelAnimationFrame(animRef.current);
  }, [isRecording, speed]);

  useEffect(() => {
    posRef.current = 0;
    if (ref.current) ref.current.scrollTop = 0;
  }, [script]);

  return (
    <div style={{
      position: "absolute", bottom: 0, left: 0, right: 0, height: "55%",
      background: "linear-gradient(transparent 0%, rgba(0,0,0,0.85) 20%)",
      padding: "28px 18px 14px", overflow: "hidden", pointerEvents: "none",
    }}>
      <div style={{ position: "absolute", top: 34, left: 18, right: 18, height: 2, background: "rgba(255,80,80,0.5)", borderRadius: 1, zIndex: 2 }} />
      <div ref={ref} style={{ overflow: "hidden", height: "100%" }}>
        <div style={{ fontSize: 14, lineHeight: 1.9, color: "rgba(255,255,255,0.95)", fontFamily: "'DM Sans', sans-serif", whiteSpace: "pre-line", paddingTop: 4, textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
          {script}
        </div>
      </div>
      {isRecording && (
        <div style={{ position: "absolute", bottom: 10, right: 14, fontSize: 10, color: "rgba(255,255,255,0.35)", fontFamily: "'DM Sans', sans-serif" }}>
          Auto-scrolling
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function VideoIntroRecorder({ onSave, onSkip, defaultCategory = null, defaultSubcategory = null }) {

  const categories     = Object.keys(SERVICE_MAP);
  const initCategory   = defaultCategory && SERVICE_MAP[defaultCategory] ? defaultCategory : categories[0];
  const initSubs       = SERVICE_MAP[initCategory].subcategories;
  const initSub        = defaultSubcategory && initSubs.includes(defaultSubcategory) ? defaultSubcategory : initSubs[0];
  const initScript     = SERVICE_MAP[initCategory]?.scripts?.[initSub] || GENERAL_SCRIPT;

  const [phase,           setPhase]           = useState("select");
  const [category,        setCategory]        = useState(initCategory);
  const [subcategory,     setSubcategory]      = useState(initSub);
  const [script,          setScript]          = useState(initScript);
  const [editingScript,   setEditingScript]   = useState(false);
  const [scrollSpeed,     setScrollSpeed]     = useState(1);
  const [showPrompt,      setShowPrompt]      = useState(true);
  const [seconds,         setSeconds]         = useState(0);
  const [recordedUrl,     setRecordedUrl]     = useState(null);
  const [recordedBlob,    setRecordedBlob]    = useState(null);
  const [errMsg,          setErrMsg]          = useState("");

  const videoRef   = useRef(null);
  const playbackRef= useRef(null);
  const streamRef  = useRef(null);
  const mediaRef   = useRef(null);
  const chunksRef  = useRef([]);
  const timerRef   = useRef(null);

  function pickCategory(cat) {
    setCategory(cat);
    const subs = SERVICE_MAP[cat].subcategories;
    const sub  = subs[0];
    setSubcategory(sub);
    setScript(SERVICE_MAP[cat]?.scripts?.[sub] || GENERAL_SCRIPT);
    setEditingScript(false);
  }

  function pickSubcategory(sub) {
    setSubcategory(sub);
    setScript(SERVICE_MAP[category]?.scripts?.[sub] || GENERAL_SCRIPT);
    setEditingScript(false);
  }

  async function requestCamera() {
    setPhase("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setPhase("ready");
    } catch {
      setErrMsg("Camera access was denied. Please allow camera and microphone in your browser settings.");
      setPhase("error");
    }
  }

  useEffect(() => {
    if (["ready","recording","paused"].includes(phase) && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [phase]);

  useEffect(() => {
    if (phase === "preview" && playbackRef.current && recordedUrl) {
      playbackRef.current.src = recordedUrl;
    }
  }, [phase, recordedUrl]);

  function getMime() {
    return ["video/webm;codecs=vp9,opus","video/webm;codecs=vp8,opus","video/webm","video/mp4"].find(t => MediaRecorder.isTypeSupported(t)) || "";
  }

  function startRecording() {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mr = new MediaRecorder(streamRef.current, { mimeType: getMime() });
    mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = finalize;
    mr.start(250);
    mediaRef.current = mr;
    setSeconds(0); setPhase("recording");
    timerRef.current = setInterval(() => {
      setSeconds(s => { if (s + 1 >= MAX_SECONDS) { stopRec(); return MAX_SECONDS; } return s + 1; });
    }, 1000);
  }

  function pauseRec() {
    if (mediaRef.current?.state === "recording") {
      mediaRef.current.pause(); clearInterval(timerRef.current); setPhase("paused");
    }
  }

  function resumeRec() {
    if (mediaRef.current?.state === "paused") {
      mediaRef.current.resume(); setPhase("recording");
      timerRef.current = setInterval(() => {
        setSeconds(s => { if (s + 1 >= MAX_SECONDS) { stopRec(); return MAX_SECONDS; } return s + 1; });
      }, 1000);
    }
  }

  function stopRec() {
    clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  }

  function finalize() {
    const blob = new Blob(chunksRef.current, { type: getMime() });
    const url  = URL.createObjectURL(blob);
    setRecordedBlob(blob); setRecordedUrl(url);
    setPhase("preview");
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  function retake() {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl(null); setRecordedBlob(null); setSeconds(0);
    requestCamera();
  }

  function confirmSave() { if (onSave) onSave(recordedBlob, recordedUrl); setPhase("done"); }

  useEffect(() => () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
  }, []);

  const R = 28, C = 2 * Math.PI * R;
  const progress  = seconds / MAX_SECONDS;
  const ringColor = seconds > 50 ? "#e63946" : seconds > 40 ? "#c97b2a" : P;

  const wrap = { fontFamily: "'DM Sans', sans-serif", background: "#fff", minHeight: "100vh", color: "#1a1a18", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "28px 16px 80px" };
  const card = { width: "100%", maxWidth: 540, background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" };

  // ── SELECT SCREEN ──────────────────────────────────────────────────────────
  if (phase === "select") return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ background: P, padding: "22px 24px 18px" }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6 }}>Optional · 60 seconds max</div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 500, color: "#fff", lineHeight: 1.25, margin: 0 }}>
            Record your self-introduction
          </h2>
        </div>

        <div style={{ padding: "18px 22px 26px" }}>

          {/* Step 1: Service category */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>
              1. Select your service type
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {categories.map(cat => (
                <button key={cat} type="button" onClick={() => pickCategory(cat)} style={{
                  padding: "7px 13px", borderRadius: 99, fontSize: 12, fontFamily: "inherit", cursor: "pointer",
                  border: `1.5px solid ${category === cat ? P : "rgba(0,0,0,0.12)"}`,
                  background: category === cat ? P : "#fff",
                  color: category === cat ? "#fff" : "#555",
                  fontWeight: category === cat ? 600 : 400, transition: "all 0.15s",
                }}>{cat}</button>
              ))}
            </div>
          </div>

          {/* Step 2: Subcategory */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>
              2. Your specific area
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {SERVICE_MAP[category].subcategories.map(sub => (
                <button key={sub} type="button" onClick={() => pickSubcategory(sub)} style={{
                  padding: "7px 13px", borderRadius: 99, fontSize: 12, fontFamily: "inherit", cursor: "pointer",
                  border: `1.5px solid ${subcategory === sub ? P : "rgba(0,0,0,0.12)"}`,
                  background: subcategory === sub ? PL : "#fff",
                  color: subcategory === sub ? PD : "#555",
                  fontWeight: subcategory === sub ? 600 : 400, transition: "all 0.15s",
                }}>{sub}</button>
              ))}
            </div>
          </div>

          {/* Step 3: Script */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                3. Your script — read or personalise
              </div>
              <button type="button" onClick={() => setEditingScript(e => !e)} style={{
                fontSize: 11, color: P, background: PL, border: `1px solid ${PM}`,
                borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit",
              }}>{editingScript ? "✓ Done" : "✏️ Edit"}</button>
            </div>

            {editingScript ? (
              <textarea value={script} onChange={e => setScript(e.target.value)} style={{
                width: "100%", minHeight: 220, padding: "12px 14px", fontSize: 13,
                fontFamily: "inherit", lineHeight: 1.8, color: "#1a1a18", background: "#fafafa",
                border: `1.5px solid ${P}`, borderRadius: 10, outline: "none",
                resize: "vertical", boxSizing: "border-box",
              }} />
            ) : (
              <div style={{
                background: "#fafafa", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10,
                padding: "13px 15px", fontSize: 12.5, color: "#444", lineHeight: 1.85,
                whiteSpace: "pre-line", maxHeight: 220, overflowY: "auto",
              }}>{script}</div>
            )}
            <p style={{ fontSize: 11, color: "#bbb", marginTop: 5, lineHeight: 1.5 }}>
              Replace [Your Name], [City], [X years], [number] with your actual details before recording.
            </p>
          </div>

          {/* Step 4: Speed + teleprompter toggle */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                Scroll speed
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[["Slow", 0.6], ["Normal", 1], ["Fast", 1.5]].map(([l, v]) => (
                  <button key={l} type="button" onClick={() => setScrollSpeed(v)} style={{
                    flex: 1, padding: "7px 0", borderRadius: 8, fontSize: 12,
                    fontFamily: "inherit", cursor: "pointer",
                    border: `1.5px solid ${scrollSpeed === v ? P : "rgba(0,0,0,0.12)"}`,
                    background: scrollSpeed === v ? PL : "#fff",
                    color: scrollSpeed === v ? PD : "#666",
                    fontWeight: scrollSpeed === v ? 600 : 400,
                  }}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                Show script on screen
              </div>
              <div onClick={() => setShowPrompt(v => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 4 }}>
                <div style={{ width: 38, height: 22, borderRadius: 99, background: showPrompt ? P : "#ddd", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: showPrompt ? 19 : 3, transition: "left 0.2s" }} />
                </div>
                <span style={{ fontSize: 12, color: "#666" }}>{showPrompt ? "Teleprompter on" : "Teleprompter off"}</span>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div style={{ background: PL, border: `1px solid ${PM}`, borderRadius: 10, padding: "11px 14px", marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: PD, marginBottom: 6 }}>💡 Quick tips</div>
            <div style={{ fontSize: 12, color: "#666", lineHeight: 1.7 }}>
              · Look at the camera, not the screen &nbsp;·&nbsp; Speak naturally and confidently<br/>
              · Good lighting matters &nbsp;·&nbsp; 30–60 seconds is perfect
            </div>
          </div>

          <button type="button" onClick={requestCamera} style={{
            width: "100%", padding: "13px", background: P, color: "#fff", border: "none",
            borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8,
          }}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M15 10l4.55-2.27A1 1 0 0121 8.72v6.56a1 1 0 01-1.45.9L15 14"/>
              <rect x="2" y="7" width="13" height="10" rx="2"/>
            </svg>
            Open camera & start
          </button>
          <button type="button" onClick={onSkip} style={{ width: "100%", padding: "9px", background: "transparent", border: "none", fontSize: 12, color: "#ccc", cursor: "pointer", fontFamily: "inherit" }}>
            Skip this step →
          </button>
        </div>
      </div>
    </div>
  );

  // ── REQUESTING ─────────────────────────────────────────────────────────────
  if (phase === "requesting") return (
    <div style={wrap}>
      <div style={{ ...card, padding: 40, textAlign: "center" }}>
        <div style={{ width: 42, height: 42, borderRadius: "50%", border: `3px solid ${PL}`, borderTop: `3px solid ${P}`, animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <p style={{ fontSize: 13, color: "#888" }}>Requesting camera access…</p>
      </div>
    </div>
  );

  // ── ERROR ──────────────────────────────────────────────────────────────────
  if (phase === "error") return (
    <div style={wrap}>
      <div style={{ ...card, padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 38, marginBottom: 12 }}>🚫</div>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, color: "#1a1a18", marginBottom: 8 }}>Camera not available</h3>
        <p style={{ fontSize: 13, color: "#888", marginBottom: 22, lineHeight: 1.6 }}>{errMsg}</p>
        <button type="button" onClick={onSkip} style={{ padding: "12px 28px", background: P, color: "#fff", border: "none", borderRadius: 10, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
          Skip video intro
        </button>
      </div>
    </div>
  );

  // ── RECORDING SCREEN ───────────────────────────────────────────────────────
  if (["ready","recording","paused"].includes(phase)) return (
    <div style={wrap}>
      <div style={{ ...card, maxWidth: 580 }}>
        <div style={{ padding: "13px 18px", borderBottom: "1px solid rgba(0,0,0,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <span style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 500, color: PD }}>YU<span style={{ color: P }}>UKKE</span></span>
            <span style={{ fontSize: 11, color: "#bbb", marginLeft: 8 }}>{subcategory}</span>
          </div>
          <span style={{ fontSize: 11, color: "#aaa" }}>Max {fmt(MAX_SECONDS)}</span>
        </div>

        <div style={{ position: "relative", background: "#0a0a0a", aspectRatio: "16/9", overflow: "hidden" }}>
          <video ref={videoRef} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transform: "scaleX(-1)" }} />

          {phase === "recording" && (
            <div style={{ position: "absolute", top: 12, left: 12, display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,0.6)", borderRadius: 99, padding: "4px 12px 4px 8px" }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#e63946", animation: "blink 1s infinite" }} />
              <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.2}}`}</style>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: "0.05em" }}>REC</span>
            </div>
          )}
          {phase === "paused" && (
            <div style={{ position: "absolute", top: 12, left: 12, background: "rgba(0,0,0,0.6)", borderRadius: 99, padding: "4px 12px" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>⏸ PAUSED</span>
            </div>
          )}

          {(phase === "recording" || phase === "paused") && (
            <div style={{ position: "absolute", top: 8, right: 12 }}>
              <svg width="62" height="62" viewBox="0 0 70 70">
                <circle cx="35" cy="35" r={R} fill="rgba(0,0,0,0.5)" stroke="rgba(255,255,255,0.1)" strokeWidth="5" />
                <circle cx="35" cy="35" r={R} fill="none" stroke={ringColor} strokeWidth="5"
                  strokeDasharray={C} strokeDashoffset={C * (1 - progress)}
                  strokeLinecap="round" transform="rotate(-90 35 35)" />
                <text x="35" y="35" textAnchor="middle" dominantBaseline="central" style={{ fill: "#fff", fontSize: 11, fontFamily: "DM Sans", fontWeight: 600 }}>
                  {fmt(seconds)}
                </text>
              </svg>
            </div>
          )}

          {phase === "ready" && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)" }}>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", marginBottom: 4 }}>📷 Camera ready</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>Press record when you're ready</div>
            </div>
          )}

          {showPrompt && <Teleprompter script={script} isRecording={phase === "recording"} speed={scrollSpeed} />}
        </div>

        <div style={{ padding: "16px 20px 20px" }}>
          {phase === "ready" && (
            <>
              {showPrompt && <div style={{ fontSize: 12, color: "#888", marginBottom: 10, textAlign: "center" }}>📜 Script scrolls automatically when you start recording</div>}
              <button type="button" onClick={startRecording} style={{ width: "100%", padding: "13px", background: P, color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#fff" }} /> Start recording
              </button>
            </>
          )}

          {(phase === "recording" || phase === "paused") && (
            <div style={{ display: "flex", gap: 10 }}>
              {phase === "recording"
                ? <button type="button" onClick={pauseRec} style={{ flex: 1, padding: "11px", background: "#f0f0ee", color: "#555", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>⏸ Pause</button>
                : <button type="button" onClick={resumeRec} style={{ flex: 1, padding: "11px", background: "#f0f0ee", color: "#555", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>▶ Resume</button>
              }
              <button type="button" onClick={stopRec} style={{ flex: 1, padding: "11px", background: P, color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>⏹ Stop & preview</button>
            </div>
          )}

          <button type="button" onClick={() => { streamRef.current?.getTracks().forEach(t=>t.stop()); streamRef.current=null; setPhase("select"); }} style={{ width: "100%", marginTop: 8, padding: "8px", background: "transparent", border: "none", fontSize: 11, color: "#ccc", cursor: "pointer", fontFamily: "inherit" }}>
            ← Change script / area
          </button>
        </div>
      </div>
    </div>
  );

  // ── PREVIEW ────────────────────────────────────────────────────────────────
  if (phase === "preview") return (
    <div style={wrap}>
      <div style={{ ...card, maxWidth: 580 }}>
        <div style={{ padding: "16px 22px 12px", borderBottom: "1px solid rgba(0,0,0,0.07)" }}>
          <div style={{ fontSize: 10, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Preview your recording</div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 19, fontWeight: 500, color: "#1a1a18", margin: 0 }}>Happy with this? Save it.</h3>
        </div>

        <div style={{ background: "#0a0a0a", aspectRatio: "16/9", overflow: "hidden" }}>
          <video ref={playbackRef} controls playsInline style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>

        <div style={{ padding: "10px 18px", background: "#fafafa", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#1a9e5e" }} />
          <span style={{ fontSize: 12, color: "#666" }}>Length: {fmt(seconds)}</span>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#aaa" }}>{recordedBlob ? `${(recordedBlob.size/1024/1024).toFixed(1)} MB` : ""}</span>
        </div>

        <div style={{ padding: "16px 22px 24px" }}>
          <div style={{ marginBottom: 16 }}>
            {["Can clients clearly see your face?","Is the audio clear enough?","Did you cover your service and strengths?"].map(q => (
              <div key={q} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${P}`, background: PL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: P, flexShrink: 0 }}>✓</div>
                <span style={{ fontSize: 13, color: "#555" }}>{q}</span>
              </div>
            ))}
          </div>
          <button type="button" onClick={confirmSave} style={{ width: "100%", padding: "13px", background: P, color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", marginBottom: 8 }}>✅ Save this video</button>
          <button type="button" onClick={retake} style={{ width: "100%", padding: "11px", background: "#f0f0ee", color: "#555", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", marginBottom: 8 }}>🔄 Retake video</button>
          <button type="button" onClick={onSkip} style={{ width: "100%", padding: "8px", background: "transparent", border: "none", fontSize: 12, color: "#ccc", cursor: "pointer", fontFamily: "inherit" }}>Skip video intro</button>
        </div>
      </div>
    </div>
  );

  // ── DONE ───────────────────────────────────────────────────────────────────
  if (phase === "done") return (
    <div style={wrap}>
      <div style={{ ...card, padding: 40, textAlign: "center" }}>
        <div style={{ width: 58, height: 58, borderRadius: "50%", background: P, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="26" height="26" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 21, color: "#1a1a18", marginBottom: 8 }}>Video saved!</h3>
        <p style={{ fontSize: 13, color: "#888", lineHeight: 1.65, maxWidth: 280, margin: "0 auto" }}>
          Your intro video has been saved. Clients and corporate partners will see it on your profile.
        </p>
      </div>
    </div>
  );

  return null;
}